/** Where the reading of an uploaded price list has got to. */
export type RateDocumentStatus = 'Analyzing' | 'PendingReview' | 'Confirmed' | 'Failed';

/** What a catalogue line is: a part, an hour of work, or a callout. */
export type RateItemKind = 'Material' | 'Labour' | 'Travel';

/** Where a price came from. `Corrected` and `Manual` outrank any later upload. */
export type RateItemSource = 'Uploaded' | 'Corrected' | 'Manual';

/** An uploaded price list. */
export interface IProviderRateDocument {
  id: number;
  fileName: string;
  contentType: string;
  status: RateDocumentStatus;
  /** What the provider answered on upload. Decides whether VAT is added on the way to the review screen. */
  pricesIncludeVat: boolean;
  currencyCode: string;
  itemsExtracted: number;
  itemsConfirmed: number;
  /** Why the reading failed, in words meant to be shown. */
  failureReason?: string;
  uploadedAt: string;
  confirmedAt?: string;
}

/** One row the parser believes it found. Nothing here is a real price until it is confirmed. */
export interface IParsedRateRow {
  reference?: string;
  kind: RateItemKind;
  title: string;
  subtitle?: string;
  category?: string;
  unit: string;
  /** As printed in the document. Null when the parser found no readable price -- it never invents one. */
  unitPrice?: number;
  /** The same price with VAT applied, which is what the catalogue stores and what the screen shows. */
  unitPriceWithVat?: number;
  /** 0..1. The server already sorts worst-first; this is why a row is where it is. */
  confidence: number;
  warning?: string;
  /** The provider already corrected this price by hand. The upload will leave it alone. */
  conflictsWithCorrectedPrice?: number;
}

export interface IProviderRateDocumentReview {
  document: IProviderRateDocument;
  vatRatePercent: number;
  rows: IParsedRateRow[];
}

/**
 * One row as the provider left it before confirming.
 *
 * `unitPrice` is **VAT included** -- the figure shown on screen, not the one
 * printed in the document. Sending the wrong one leaves a whole catalogue
 * under what its owner meant to charge, with nothing visible to show for it.
 */
export interface IConfirmRateRow {
  reference?: string;
  kind: RateItemKind;
  title: string;
  subtitle?: string;
  category?: string;
  unit: string;
  unitPrice: number;
}

export interface IConfirmRateSheetResult {
  created: number;
  updated: number;
  /** Left alone because the provider had corrected that price by hand. Reported, never silent. */
  skippedManuallyCorrected: number;
  document: IProviderRateDocument;
}

/** One line of the provider's own catalogue. */
export interface IProviderRateItem {
  id: number;
  catalogKey: string;
  reference?: string;
  kind: RateItemKind;
  title: string;
  subtitle?: string;
  category?: string;
  unit: string;
  /** VAT included -- what they charge, not what they pay at the merchant's counter. */
  unitPrice: number;
  currencyCode: string;
  source: RateItemSource;
  timesUsed: number;
  lastUsedAt?: string;
  updatedAt: string;
}

export interface IUpsertRateItemRequest {
  reference?: string;
  kind: RateItemKind;
  title: string;
  subtitle?: string;
  category?: string;
  unit: string;
  unitPrice: number;
}

/** The units a trade price list actually uses. Free text is still accepted; these are the quick picks. */
export const RATE_ITEM_UNITS = ['ud', 'h', 'm', 'm2', 'm3', 'kg', 'l'] as const;
